import { css } from "@emotion/css";
import { Button, Callout, Dialog, DialogActions, DialogContent } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { MachineResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import CopyValueToClipboard from "~/components/CopyToClipboardButton/CopyValueToClipboard";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { Note } from "~/components/form";
type RemoveOctopusPermissionsControllerDialogProps = {
    open: boolean;
    closeDialog: () => void;
    machine: MachineResource;
};
export function RemoveOctopusPermissionsControllerDialog({ open, closeDialog, machine }: RemoveOctopusPermissionsControllerDialogProps) {
    const helmUninstallCommand = `helm uninstall --namespace octopus-permissions-controller-system octopus-permissions-controller`;
    const deleteNamespaceCommand = `kubectl delete namespace octopus-permissions-controller-system`;
    const { navigate } = useSpaceAwareNavigation();
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create health check task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForMachine(machine),
        afterComplete: async (_, createdTask) => navigate(links.taskPage.generateUrl({ taskId: createdTask.Id })),
    });
    return (<Dialog open={open}>
            <DialogContent>
                <Callout type="warning" title="Warning">
                    Removing the Octopus Permissions controller may affect other Kubernetes agents in your cluster.
                </Callout>
                <div className={styles.container}>
                    <p className={styles.boldText}>Steps required:</p>
                    <p>To fully remove Octopus Permissions controller from your cluster, execute the following commands:</p>
                    <ol className={styles.listStyle}>
                        <li>
                            <CopyValueToClipboard value={helmUninstallCommand}>
                                <code>{helmUninstallCommand}</code>
                            </CopyValueToClipboard>
                        </li>
                        <li>
                            <CopyValueToClipboard value={deleteNamespaceCommand}>
                                <code>{deleteNamespaceCommand}</code>
                            </CopyValueToClipboard>
                        </li>
                    </ol>
                </div>
                <Note>If you customized the installation by changing the release name or namespace, please update the commands accordingly.</Note>
                <Note>Once you've run the commands, run a health check on your agent to update the detected configuration.</Note>
                <DialogActions>
                    <Button size="medium" importance="secondary" onClick={closeDialog} label="Close"/>
                    <Button size="medium" importance="primary" onClick={createHealthCheckTask} label="Run health check" disabled={isCreatingHealthCheckTask}/>
                </DialogActions>
            </DialogContent>
        </Dialog>);
}
const styles = {
    container: css({
        marginTop: space[24],
    }),
    boldText: css({
        font: text.body.bold.medium,
    }),
    listStyle: css({
        listStyleType: "decimal",
        paddingLeft: space[16],
    }),
};
